import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { fetchHealthSummaryData } from '../services/healthSummaryService';
import { getDocumentUrl } from '../services/documentService';
import { listHealthUpdates } from '../services/healthUpdateService';
import type { HealthSummaryData, AppendixFile } from '../types/healthSummary';
import type { HealthUpdate, UpdatableCategory, CategorySchema } from '../types/healthUpdate';
import { UPDATABLE_CATEGORIES, CATEGORY_IDENTITY_FIELDS } from '../types/healthUpdate';
import PatientUpdateModal from '../components/PatientUpdateModal';

const HealthSummary: React.FC = () => {
  const { user } = useAuth();
  const [data, setData] = useState<HealthSummaryData | null>(null);
  const [uploadDate, setUploadDate] = useState<Date | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [patientUpdates, setPatientUpdates] = useState<HealthUpdate[]>([]);
  const [appendices, setAppendices] = useState<AppendixFile[]>([]);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    setError('');
    try {
      const [result, updatesResult] = await Promise.all([
        fetchHealthSummaryData(user?.userId || '').catch(() => null),
        listHealthUpdates().catch(() => ({ updates: [] })),
      ]);
      setData(result?.data ?? null);
      setUploadDate(result?.uploadDate ?? null);
      setAppendices(result?.appendices ?? []);
      setPatientUpdates(updatesResult.updates);
    } catch (err: any) {
      setError(err.message || 'Failed to load health summary');
    } finally {
      setLoading(false);
    }
  };

  const getUpdatesForCategory = (category: UpdatableCategory): HealthUpdate[] => {
    return patientUpdates.filter(u => u.category === category);
  };

  const isReplacedByUpdate = (category: UpdatableCategory, adminRow: Record<string, any>): boolean => {
    const identityFields = CATEGORY_IDENTITY_FIELDS[category];
    if (!identityFields || identityFields.length === 0) return false;
    const updates = getUpdatesForCategory(category);
    return updates.some(u =>
      identityFields.every(field => {
        const adminVal = String(adminRow[field] || '').trim().toLowerCase();
        const updateVal = String(u.data[field] || '').trim().toLowerCase();
        return adminVal === updateVal && adminVal.length > 0;
      })
    );
  };

  const getCategorySchema = (key: UpdatableCategory): CategorySchema | undefined => {
    return UPDATABLE_CATEGORIES.find(c => c.key === key);
  };

  const handleAddEntry = (_category: UpdatableCategory, e: React.MouseEvent) => {
    e.stopPropagation();
    setShowModal(true);
  };

  const handleModalSubmitted = () => {
    setShowModal(false);
    listHealthUpdates()
      .then(result => setPatientUpdates(result.updates))
      .catch(() => {});
  };

  const [collapsedSections, setCollapsedSections] = useState<Set<string>>(new Set([
    'personal', 'conditions', 'medications', 'diagnostics', 'insurance', 'familyHistory', 'careTeam', 'appendices',
  ]));

  const toggleSection = (section: string) => {
    setCollapsedSections(prev => {
      const next = new Set(prev);
      if (next.has(section)) {
        next.delete(section);
      } else {
        next.add(section);
      }
      return next;
    });
  };

  const isSectionOpen = (section: string) => !collapsedSections.has(section);

  const printStateRef = useRef<Set<string> | null>(null);

  const handlePrint = () => {
    // Save current state and expand all
    printStateRef.current = new Set(collapsedSections);
    setCollapsedSections(new Set());
  };

  // After sections expand for printing, trigger print then restore
  useEffect(() => {
    if (printStateRef.current !== null && collapsedSections.size === 0) {
      const prev = printStateRef.current;
      printStateRef.current = null;
      // Give the browser a frame to paint the expanded content
      setTimeout(() => {
        window.print();
        setCollapsedSections(prev);
      }, 100);
    }
  }, [collapsedSections]);

  const hasAnyData = (data && (
    data.personalIdentification.length > 0 ||
    data.demographics.length > 0 ||
    data.emergencyContacts.length > 0 ||
    data.familyDoctor.length > 0 ||
    data.conditions.length > 0 ||
    data.allergies.length > 0 ||
    data.medications.length > 0 ||
    data.vaccineHistory.length > 0 ||
    data.diagnosticHistory.length > 0 ||
    data.surgicalHistory.length > 0 ||
    data.travelHistory.length > 0 ||
    data.insuranceCarePlanning.length > 0 ||
    data.familyHistory.length > 0 ||
    data.careTeam.length > 0
  )) || patientUpdates.length > 0;

  const addEntryButton = (category: UpdatableCategory) => (
    <button
      className="hs-add-entry-btn"
      onClick={(e) => handleAddEntry(category, e)}
      title={`Add new ${getCategorySchema(category)?.label || ''} entry`}
    >
      + Add Entry
    </button>
  );

  const patientBadge = () => (
    <span className="hs-patient-badge">Patient-reported</span>
  );

  const sectionHasData = (adminData: any[], category?: UpdatableCategory): boolean => {
    if (adminData.length > 0) return true;
    if (category && getUpdatesForCategory(category).length > 0) return true;
    return false;
  };

  const chevronIcon = (section: string) => (
    <svg className={`hs-chevron ${isSectionOpen(section) ? 'open' : ''}`} width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
      <polyline points="9 18 15 12 9 6"/>
    </svg>
  );

  const emptyState = (icon: React.ReactNode) => (
    <div className="hs-empty-state">
      {icon}
      <div>There is currently no information available for this section.</div>
    </div>
  );

  const handleViewAppendix = async (key: string) => {
    const url = await getDocumentUrl(key);
    window.open(url, '_blank');
  };

  const renderWithAppendixLinks = (text: string): React.ReactNode => {
    if (!text || appendices.length === 0) return text;
    const regex = /Appendix\s+[A-Z]/gi;
    const parts: React.ReactNode[] = [];
    let lastIndex = 0;
    let match: RegExpExecArray | null;
    while ((match = regex.exec(text)) !== null) {
      const matchText = match[0];
      const normalizedLabel = matchText.replace(/\s+/g, ' ').replace(/appendix/i, 'Appendix').trim();
      const hasFile = appendices.some(a => a.label.toLowerCase() === normalizedLabel.toLowerCase());
      if (match.index > lastIndex) {
        parts.push(text.slice(lastIndex, match.index));
      }
      if (hasFile) {
        parts.push(
          <button
            key={match.index}
            className="hs-appendix-link"
            onClick={() => {
              setCollapsedSections(prev => {
                const next = new Set(prev);
                next.delete('appendices');
                return next;
              });
              setTimeout(() => {
                document.getElementById('hs-section-appendices')?.scrollIntoView({ behavior: 'smooth' });
              }, 100);
            }}
          >
            {matchText}
          </button>
        );
      } else {
        parts.push(matchText);
      }
      lastIndex = match.index + matchText.length;
    }
    if (lastIndex < text.length) {
      parts.push(text.slice(lastIndex));
    }
    return parts.length > 0 ? <>{parts}</> : text;
  };

  const patientName = user ? `${user.firstName || ''} ${user.lastName || ''}`.trim() : '';

  // Use the most recent date between the Excel upload and the latest patient update
  const latestUpdateDate = patientUpdates.length > 0
    ? patientUpdates.reduce((latest, u) => {
        const d = new Date(u.submittedAt).getTime();
        return d > latest ? d : latest;
      }, 0)
    : 0;
  const effectiveDate = (() => {
    const excelTime = uploadDate ? uploadDate.getTime() : 0;
    if (excelTime && latestUpdateDate) return new Date(Math.max(excelTime, latestUpdateDate));
    if (latestUpdateDate) return new Date(latestUpdateDate);
    if (excelTime) return uploadDate;
    return null;
  })();
  const formattedDate = effectiveDate
    ? effectiveDate.toLocaleDateString('en-CA', { year: 'numeric', month: 'long', day: 'numeric' })
    : null;

  return (
    <div className="health-summary">

      {error && <div className="error-message">{error}</div>}

      {loading ? (
        <p className="loading-text">Loading health summary...</p>
      ) : hasAnyData ? (
        <>
          {/* Document Header Banner */}
          <div className="hs-doc-header">
            <div className="hs-doc-banner">
              <div className="hs-doc-banner-title">My Health Summary</div>
              <div className="hs-doc-banner-sub">Your health profile, available anytime, anywhere.</div>
            </div>
            <div className="hs-doc-owner">
              <div className="hs-doc-owner-label">This Personal Health Summary belongs to:</div>
              <div className="hs-doc-owner-name">{patientName || 'Patient'}</div>
              {formattedDate && (
                <div className="hs-doc-updated">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                  This Summary was last updated on: {formattedDate}
                </div>
              )}
              <div className="hs-doc-disclaimer">Note: Health information may change. This Summary may not reflect changes after the date shown.</div>
            </div>
            <div className="hs-doc-actions">
              <button className="hs-doc-btn hs-doc-btn-outline" onClick={handlePrint}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="6 9 6 2 18 2 18 9"/><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/><rect x="6" y="14" width="12" height="8"/></svg>
                Print Summary
              </button>
              <button className="hs-doc-btn hs-doc-btn-primary" onClick={() => setShowModal(true)}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                Request a Change
              </button>
            </div>
          </div>

          <div className="hs-sections">

            {/* A. Personal & Demographic Information */}
            <div className="hs-section" id="hs-section-personal">
              <button className={`hs-section-title ${isSectionOpen('personal') ? 'open' : ''}`} onClick={() => toggleSection('personal')}>
                <span>A. Personal &amp; Demographic Information</span>
                <span className="hs-title-actions">
                  {addEntryButton('emergencyContacts')}
                  {chevronIcon('personal')}
                </span>
              </button>
              {isSectionOpen('personal') && (
                <div className="hs-section-body">
                  {/* Personal Identification */}
                  {data && data.personalIdentification.length > 0 && (
                    <>
                      <div className="hs-sub-heading">Personal Identification</div>
                      <div className="hs-personal-grid">
                        {data.personalIdentification.map((item, i) => (
                          <div key={i} className="hs-personal-card">
                            <span className="hs-field-label">{item.field}</span>
                            <span className="hs-field-value">{item.value || 'N/A'}</span>
                          </div>
                        ))}
                      </div>
                    </>
                  )}

                  {/* Demographics */}
                  {data && data.demographics.length > 0 && (
                    <>
                      <div className="hs-divider" />
                      <div className="hs-sub-heading">Demographics</div>
                      <div className="hs-personal-grid">
                        {data.demographics.map((item, i) => (
                          <div key={i} className="hs-personal-card">
                            <span className="hs-field-label">{item.field}</span>
                            <span className="hs-field-value">{item.value || 'N/A'}</span>
                          </div>
                        ))}
                      </div>
                    </>
                  )}

                  {/* Emergency Contacts */}
                  {sectionHasData((data?.emergencyContacts || []).filter(c => !isReplacedByUpdate('emergencyContacts', c)), 'emergencyContacts') ? (
                    <>
                      <div className="hs-divider" />
                      <div className="hs-sub-row">
                        <div>
                          <div className="hs-sub-heading">Emergency Contacts</div>
                        </div>
                      </div>
                      <div className="hs-table-wrapper">
                        <table className="hs-table">
                          <thead>
                            <tr>
                              <th>Name</th>
                              <th>Relationship</th>
                              <th>Phone</th>
                              <th>Email</th>
                              <th></th>
                            </tr>
                          </thead>
                          <tbody>
                            {(data?.emergencyContacts || []).filter(c => !isReplacedByUpdate('emergencyContacts', c)).map((c, i) => (
                              <tr key={i}>
                                <td>{c.name}</td>
                                <td>{c.relationship || 'N/A'}</td>
                                <td>{c.phone || 'N/A'}</td>
                                <td>{c.email || 'N/A'}</td>
                                <td></td>
                              </tr>
                            ))}
                            {getUpdatesForCategory('emergencyContacts').map((u, i) => (
                              <tr key={`pu-${i}`} className="hs-patient-row">
                                <td>{u.data.name || 'N/A'}</td>
                                <td>{u.data.relationship || 'N/A'}</td>
                                <td>{u.data.phone || 'N/A'}</td>
                                <td>{u.data.email || 'N/A'}</td>
                                <td>{patientBadge()}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="hs-divider" />
                      <div className="hs-sub-heading">Emergency Contacts</div>
                      {emptyState(
                        <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="var(--border)" strokeWidth="1.5"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>

            {/* B. Medical Conditions & Allergies */}
            <div className="hs-section" id="hs-section-conditions">
              <button className={`hs-section-title ${isSectionOpen('conditions') ? 'open' : ''}`} onClick={() => toggleSection('conditions')}>
                <span>B. Medical Conditions &amp; Allergies</span>
                <span className="hs-title-actions">
                  {addEntryButton('conditions')}
                  {addEntryButton('allergies')}
                  {chevronIcon('conditions')}
                </span>
              </button>
              {isSectionOpen('conditions') && (
                <div className="hs-section-body">
                  {/* Active Medical Conditions */}
                  {sectionHasData((data?.conditions || []).filter(c => !isReplacedByUpdate('conditions', c)), 'conditions') ? (
                    <>
                      <div className="hs-sub-heading">Active Medical Conditions</div>
                      <div className="hs-sub-desc">On-going / high risk conditions</div>
                      <div className="hs-table-wrapper">
                        <table className="hs-table">
                          <thead>
                            <tr>
                              <th>Condition</th>
                              <th>Date of Onset</th>
                              <th>Recent Reading</th>
                              <th>Comments</th>
                              <th></th>
                            </tr>
                          </thead>
                          <tbody>
                            {(data?.conditions || []).filter(c => !isReplacedByUpdate('conditions', c)).map((c, i) => (
                              <tr key={i}>
                                <td>{c.condition}</td>
                                <td>{c.dateOfOnset || 'N/A'}</td>
                                <td>{c.recentReading || 'N/A'}</td>
                                <td>{c.comments ? renderWithAppendixLinks(c.comments) : 'N/A'}</td>
                                <td></td>
                              </tr>
                            ))}
                            {getUpdatesForCategory('conditions').map((u, i) => (
                              <tr key={`pu-${i}`} className="hs-patient-row">
                                <td>{u.data.condition || 'N/A'}</td>
                                <td>{u.data.dateOfOnset || 'N/A'}</td>
                                <td>{u.data.recentReading || 'N/A'}</td>
                                <td>{u.data.comments || 'N/A'}</td>
                                <td>{patientBadge()}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="hs-sub-heading">Active Medical Conditions</div>
                      {emptyState(
                        <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="var(--border)" strokeWidth="1.5"><path d="M22 12h-4l-3 9L9 3l-3 9H2"/></svg>
                      )}
                    </>
                  )}

                  {/* Allergies & Adverse Reactions */}
                  <div className="hs-divider" />
                  {sectionHasData((data?.allergies || []).filter(a => !isReplacedByUpdate('allergies', a)), 'allergies') ? (
                    <>
                      <div className="hs-sub-heading">Allergies &amp; Adverse Reactions</div>
                      <div className="hs-table-wrapper">
                        <table className="hs-table">
                          <thead>
                            <tr>
                              <th>Allergen / Agent</th>
                              <th>Reaction Type</th>
                              <th>Severity</th>
                              <th>Comments</th>
                              <th></th>
                            </tr>
                          </thead>
                          <tbody>
                            {(data?.allergies || []).filter(a => !isReplacedByUpdate('allergies', a)).map((a, i) => (
                              <tr key={i}>
                                <td>{a.allergen}</td>
                                <td>{a.reactionType || 'N/A'}</td>
                                <td>
                                  <span className={`hs-severity hs-severity-${a.severity.toLowerCase()}`}>
                                    {a.severity || 'N/A'}
                                  </span>
                                </td>
                                <td>{a.comments || 'N/A'}</td>
                                <td></td>
                              </tr>
                            ))}
                            {getUpdatesForCategory('allergies').map((u, i) => (
                              <tr key={`pu-${i}`} className="hs-patient-row">
                                <td>{u.data.allergen || 'N/A'}</td>
                                <td>{u.data.reactionType || 'N/A'}</td>
                                <td>
                                  {u.data.severity ? (
                                    <span className={`hs-severity hs-severity-${u.data.severity.toLowerCase()}`}>
                                      {u.data.severity}
                                    </span>
                                  ) : 'N/A'}
                                </td>
                                <td>{u.data.comments || 'N/A'}</td>
                                <td>{patientBadge()}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="hs-sub-heading">Allergies &amp; Adverse Reactions</div>
                      {emptyState(
                        <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="var(--border)" strokeWidth="1.5"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>

            {/* C. Medications & Vaccinations */}
            <div className="hs-section" id="hs-section-medications">
              <button className={`hs-section-title ${isSectionOpen('medications') ? 'open' : ''}`} onClick={() => toggleSection('medications')}>
                <span>C. Medications &amp; Vaccinations</span>
                <span className="hs-title-actions">
                  {addEntryButton('medications')}
                  {addEntryButton('vaccineHistory')}
                  {chevronIcon('medications')}
                </span>
              </button>
              {isSectionOpen('medications') && (
                <div className="hs-section-body">
                  {/* Recent Medications */}
                  {sectionHasData((data?.medications || []).filter(m => !isReplacedByUpdate('medications', m)), 'medications') ? (
                    <>
                      <div className="hs-sub-desc" style={{ fontStyle: 'italic', marginBottom: '8px' }}>Note: This reflects records provided by the patient and pharmacy team. It may not include all medications dispensed or prescribed.</div>
                      <div className="hs-sub-heading">Recent Medications</div>
                      <div className="hs-sub-desc">As of last dispense — for medication history, please see Appendix A</div>
                      <div className="hs-table-wrapper">
                        <table className="hs-table">
                          <thead>
                            <tr>
                              <th>Medication</th>
                              <th>Dosage / Frequency</th>
                              <th>First Dispensed</th>
                              <th>Last Dispensed</th>
                              <th>Prescriber</th>
                              <th></th>
                            </tr>
                          </thead>
                          <tbody>
                            {(data?.medications || []).filter(m => !isReplacedByUpdate('medications', m)).map((m, i) => (
                              <tr key={i}>
                                <td>{m.name}</td>
                                <td>{m.dosageFrequency || 'N/A'}</td>
                                <td>{m.dateFirstDispensed || 'N/A'}</td>
                                <td>{m.dateLastDispensed || 'N/A'}</td>
                                <td>{m.prescribingProvider || 'N/A'}</td>
                                <td></td>
                              </tr>
                            ))}
                            {getUpdatesForCategory('medications').map((u, i) => (
                              <tr key={`pu-${i}`} className="hs-patient-row">
                                <td>{u.data.name || 'N/A'}</td>
                                <td>{u.data.dosageFrequency || 'N/A'}</td>
                                <td>{u.data.dateFirstDispensed || 'N/A'}</td>
                                <td>{u.data.dateLastDispensed || 'N/A'}</td>
                                <td>{u.data.prescribingProvider || 'N/A'}</td>
                                <td>{patientBadge()}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="hs-sub-heading">Recent Medications</div>
                      {emptyState(
                        <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="var(--border)" strokeWidth="1.5"><circle cx="8" cy="18" r="3"/><circle cx="16" cy="18" r="3"/><path d="M11 18h-0.5a4 4 0 0 1-4-4V8a4 4 0 0 1 4-4h3a4 4 0 0 1 4 4v6a4 4 0 0 1-4 4H13"/></svg>
                      )}
                    </>
                  )}

                  {/* Vaccination History */}
                  <div className="hs-divider" />
                  {sectionHasData(data?.vaccineHistory || [], 'vaccineHistory') ? (
                    <>
                      <div className="hs-sub-heading">Vaccination History</div>
                      <div className="hs-sub-desc">Based on available records</div>
                      <div className="hs-table-wrapper">
                        <table className="hs-table">
                          <thead>
                            <tr>
                              <th>Vaccine</th>
                              <th>Dosage</th>
                              <th>Date</th>
                              <th>Provider</th>
                              <th>Source</th>
                              <th></th>
                            </tr>
                          </thead>
                          <tbody>
                            {(data?.vaccineHistory || []).map((v, i) => (
                              <tr key={i}>
                                <td>{v.vaccine}</td>
                                <td>{v.dosage || 'N/A'}</td>
                                <td>{v.date || 'N/A'}</td>
                                <td>{v.provider || 'N/A'}</td>
                                <td>{v.source || 'N/A'}</td>
                                <td></td>
                              </tr>
                            ))}
                            {getUpdatesForCategory('vaccineHistory').map((u, i) => (
                              <tr key={`pu-${i}`} className="hs-patient-row">
                                <td>{u.data.vaccine || 'N/A'}</td>
                                <td>{u.data.dosage || 'N/A'}</td>
                                <td>{u.data.date || 'N/A'}</td>
                                <td>{u.data.provider || 'N/A'}</td>
                                <td>{u.data.source || 'N/A'}</td>
                                <td>{patientBadge()}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="hs-sub-heading">Vaccination History</div>
                      {emptyState(
                        <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="var(--border)" strokeWidth="1.5"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>

            {/* D. Diagnostics, Surgeries & Travel History */}
            <div className="hs-section" id="hs-section-diagnostics">
              <button className={`hs-section-title ${isSectionOpen('diagnostics') ? 'open' : ''}`} onClick={() => toggleSection('diagnostics')}>
                <span>D. Diagnostics, Surgeries &amp; Travel History</span>
                <span className="hs-title-actions">
                  {addEntryButton('surgicalHistory')}
                  {addEntryButton('travelHistory')}
                  {chevronIcon('diagnostics')}
                </span>
              </button>
              {isSectionOpen('diagnostics') && (
                <div className="hs-section-body">
                  {/* Diagnostic History */}
                  {data && data.diagnosticHistory.length > 0 ? (
                    <>
                      <div className="hs-sub-heading">Diagnostic History</div>
                      <div className="hs-sub-desc">Most recent and clinically significant</div>
                      <div className="hs-table-wrapper">
                        <table className="hs-table">
                          <thead>
                            <tr>
                              <th>Procedure</th>
                              <th>Date</th>
                              <th>Key Finding</th>
                              <th>Notes</th>
                            </tr>
                          </thead>
                          <tbody>
                            {data.diagnosticHistory.map((d, i) => (
                              <tr key={i}>
                                <td>{d.procedure}</td>
                                <td>{d.date || 'N/A'}</td>
                                <td>{d.keyFinding || 'N/A'}</td>
                                <td>{d.notes ? renderWithAppendixLinks(d.notes) : 'N/A'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="hs-sub-heading">Diagnostic History</div>
                      {emptyState(
                        <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="var(--border)" strokeWidth="1.5"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>
                      )}
                    </>
                  )}

                  {/* Surgical History */}
                  <div className="hs-divider" />
                  {sectionHasData(data?.surgicalHistory || [], 'surgicalHistory') ? (
                    <>
                      <div className="hs-sub-heading">Surgical History &amp; Hospitalizations</div>
                      <div className="hs-table-wrapper">
                        <table className="hs-table">
                          <thead>
                            <tr>
                              <th>Procedure / Hospitalization</th>
                              <th>Date</th>
                              <th>Comments</th>
                              <th></th>
                            </tr>
                          </thead>
                          <tbody>
                            {(data?.surgicalHistory || []).map((s, i) => (
                              <tr key={i}>
                                <td>{s.procedure}</td>
                                <td>{s.date || 'N/A'}</td>
                                <td>{s.comments ? renderWithAppendixLinks(s.comments) : 'N/A'}</td>
                                <td></td>
                              </tr>
                            ))}
                            {getUpdatesForCategory('surgicalHistory').map((u, i) => (
                              <tr key={`pu-${i}`} className="hs-patient-row">
                                <td>{u.data.procedure || 'N/A'}</td>
                                <td>{u.data.date || 'N/A'}</td>
                                <td>{u.data.comments || 'N/A'}</td>
                                <td>{patientBadge()}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="hs-sub-heading">Surgical History &amp; Hospitalizations</div>
                      {emptyState(
                        <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="var(--border)" strokeWidth="1.5"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M12 8v8"/><path d="M8 12h8"/></svg>
                      )}
                    </>
                  )}

                  {/* Travel History */}
                  <div className="hs-divider" />
                  {sectionHasData(data?.travelHistory || [], 'travelHistory') ? (
                    <>
                      <div className="hs-sub-heading">Travel History</div>
                      <div className="hs-sub-desc">Based on available information</div>
                      <div className="hs-table-wrapper">
                        <table className="hs-table">
                          <thead>
                            <tr>
                              <th>Start Date</th>
                              <th>End Date</th>
                              <th>City / Region</th>
                              <th>Country</th>
                              <th></th>
                            </tr>
                          </thead>
                          <tbody>
                            {(data?.travelHistory || []).map((t, i) => (
                              <tr key={i}>
                                <td>{t.startDate || 'N/A'}</td>
                                <td>{t.endDate || 'N/A'}</td>
                                <td>{t.cityRegion || 'N/A'}</td>
                                <td>{t.country || 'N/A'}</td>
                                <td></td>
                              </tr>
                            ))}
                            {getUpdatesForCategory('travelHistory').map((u, i) => (
                              <tr key={`pu-${i}`} className="hs-patient-row">
                                <td>{u.data.startDate || 'N/A'}</td>
                                <td>{u.data.endDate || 'N/A'}</td>
                                <td>{u.data.cityRegion || 'N/A'}</td>
                                <td>{u.data.country || 'N/A'}</td>
                                <td>{patientBadge()}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="hs-sub-heading">Travel History</div>
                      <div className="hs-sub-desc">Based on available information</div>
                      {emptyState(
                        <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="var(--border)" strokeWidth="1.5"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>

            {/* E. Insurance & Care Planning */}
            <div className="hs-section" id="hs-section-insurance">
              <button className={`hs-section-title ${isSectionOpen('insurance') ? 'open' : ''}`} onClick={() => toggleSection('insurance')}>
                <span>E. Insurance &amp; Care Planning</span>
                <span className="hs-title-actions">
                  {chevronIcon('insurance')}
                </span>
              </button>
              {isSectionOpen('insurance') && (
                <div className="hs-section-body">
                  {data && data.insuranceCarePlanning.length > 0 ? (
                    <>
                      <div className="hs-sub-heading">Insurance &amp; Care Planning</div>
                      <div className="hs-personal-grid">
                        {data.insuranceCarePlanning.map((item, i) => (
                          <div key={i} className="hs-personal-card">
                            <span className="hs-field-label">{item.field}</span>
                            <span className="hs-field-value">{item.value || 'N/A'}</span>
                          </div>
                        ))}
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="hs-sub-heading">Insurance &amp; Care Planning</div>
                      {emptyState(
                        <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="var(--border)" strokeWidth="1.5"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>

            {/* F. Family History */}
            <div className="hs-section" id="hs-section-familyHistory">
              <button className={`hs-section-title ${isSectionOpen('familyHistory') ? 'open' : ''}`} onClick={() => toggleSection('familyHistory')}>
                <span>F. Family History</span>
                <span className="hs-title-actions">
                  {addEntryButton('familyHistory')}
                  {chevronIcon('familyHistory')}
                </span>
              </button>
              {isSectionOpen('familyHistory') && (
                <div className="hs-section-body">
                  {sectionHasData((data?.familyHistory || []).filter(f => !isReplacedByUpdate('familyHistory', f)), 'familyHistory') ? (
                    <>
                      <div className="hs-sub-heading">Family History Details</div>
                      <div className="hs-table-wrapper">
                        <table className="hs-table">
                          <thead>
                            <tr>
                              <th>Relation</th>
                              <th>Medical Condition</th>
                              <th>Age of Onset</th>
                              <th>Provider</th>
                              <th></th>
                            </tr>
                          </thead>
                          <tbody>
                            {(() => {
                              const rows = (data?.familyHistory || []).filter(f => !isReplacedByUpdate('familyHistory', f));
                              // Group consecutive rows by relation for rowSpan
                              const rendered: React.ReactNode[] = [];
                              let i = 0;
                              while (i < rows.length) {
                                const relation = rows[i].relation;
                                let span = 1;
                                while (i + span < rows.length && rows[i + span].relation === relation) span++;
                                for (let j = 0; j < span; j++) {
                                  const f = rows[i + j];
                                  rendered.push(
                                    <tr key={i + j}>
                                      {j === 0 && <td rowSpan={span} style={{ verticalAlign: 'top', fontWeight: 600 }}>{f.relation}</td>}
                                      <td>{f.condition || 'N/A'}</td>
                                      <td>{f.ageOfOnset || 'N/A'}</td>
                                      <td>{f.provider || 'N/A'}</td>
                                      <td></td>
                                    </tr>
                                  );
                                }
                                i += span;
                              }
                              return rendered;
                            })()}
                            {getUpdatesForCategory('familyHistory').map((u, i) => (
                              <tr key={`pu-${i}`} className="hs-patient-row">
                                <td>{u.data.relation || 'N/A'}</td>
                                <td>{u.data.condition || 'N/A'}</td>
                                <td>{u.data.ageOfOnset || 'N/A'}</td>
                                <td>{u.data.provider || 'N/A'}</td>
                                <td>{patientBadge()}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="hs-sub-heading">Family History Details</div>
                      {emptyState(
                        <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="var(--border)" strokeWidth="1.5"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>

            {/* G. Care Team */}
            <div className="hs-section" id="hs-section-careTeam">
              <button className={`hs-section-title ${isSectionOpen('careTeam') ? 'open' : ''}`} onClick={() => toggleSection('careTeam')}>
                <span>G. Care Team</span>
                <span className="hs-title-actions">
                  {addEntryButton('careTeam')}
                  {addEntryButton('familyDoctor')}
                  {chevronIcon('careTeam')}
                </span>
              </button>
              {isSectionOpen('careTeam') && (
                <div className="hs-section-body">
                  {/* Care Team Contacts */}
                  {sectionHasData((data?.careTeam || []).filter(c => !isReplacedByUpdate('careTeam', c)), 'careTeam') ? (
                    <>
                      <div className="hs-sub-heading">Care Team Contacts</div>
                      <div className="hs-table-wrapper">
                        <table className="hs-table">
                          <thead>
                            <tr>
                              <th>Name</th>
                              <th>Specialty</th>
                              <th>Address</th>
                              <th>Phone</th>
                              <th>Last Visit</th>
                              <th></th>
                            </tr>
                          </thead>
                          <tbody>
                            {(data?.careTeam || []).filter(c => !isReplacedByUpdate('careTeam', c)).map((c, i) => (
                              <tr key={i}>
                                <td>{c.name}</td>
                                <td>{c.specialty || 'N/A'}</td>
                                <td>{c.address || 'N/A'}</td>
                                <td>{c.phone || 'N/A'}</td>
                                <td>{c.dateOfLastVisit || 'N/A'}</td>
                                <td></td>
                              </tr>
                            ))}
                            {getUpdatesForCategory('careTeam').map((u, i) => (
                              <tr key={`pu-${i}`} className="hs-patient-row">
                                <td>{u.data.name || 'N/A'}</td>
                                <td>{u.data.specialty || 'N/A'}</td>
                                <td>{u.data.address || 'N/A'}</td>
                                <td>{u.data.phone || 'N/A'}</td>
                                <td>{u.data.dateOfLastVisit || 'N/A'}</td>
                                <td>{patientBadge()}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="hs-sub-heading">Care Team Contacts</div>
                      {emptyState(
                        <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="var(--border)" strokeWidth="1.5"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
                      )}
                    </>
                  )}

                  {/* Family Doctor & Pharmacy */}
                  <div className="hs-divider" />
                  {sectionHasData((data?.familyDoctor || []).filter(d => !isReplacedByUpdate('familyDoctor', d)), 'familyDoctor') ? (
                    <>
                      <div className="hs-sub-heading">Family Doctor &amp; Pharmacy</div>
                      <div className="hs-table-wrapper">
                        <table className="hs-table">
                          <thead>
                            <tr>
                              <th>Role</th>
                              <th>Name</th>
                              <th>Address</th>
                              <th>Phone</th>
                              <th>Fax</th>
                              <th></th>
                            </tr>
                          </thead>
                          <tbody>
                            {(data?.familyDoctor || []).filter(d => !isReplacedByUpdate('familyDoctor', d)).map((d, i) => (
                              <tr key={i}>
                                <td>{d.role || 'N/A'}</td>
                                <td>{d.name || 'N/A'}</td>
                                <td>{d.address || 'N/A'}</td>
                                <td>{d.phone || 'N/A'}</td>
                                <td>{d.fax || 'N/A'}</td>
                                <td></td>
                              </tr>
                            ))}
                            {getUpdatesForCategory('familyDoctor').map((u, i) => (
                              <tr key={`pu-${i}`} className="hs-patient-row">
                                <td>{u.data.role || 'N/A'}</td>
                                <td>{u.data.name || 'N/A'}</td>
                                <td>{u.data.address || 'N/A'}</td>
                                <td>{u.data.phone || 'N/A'}</td>
                                <td>{u.data.fax || 'N/A'}</td>
                                <td>{patientBadge()}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="hs-sub-heading">Family Doctor &amp; Pharmacy</div>
                      {emptyState(
                        <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="var(--border)" strokeWidth="1.5"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>

            {/* H. Appendices */}
            <div className="hs-section" id="hs-section-appendices">
              <button className={`hs-section-title ${isSectionOpen('appendices') ? 'open' : ''}`} onClick={() => toggleSection('appendices')}>
                <span>H. Appendices</span>
                <span className="hs-title-actions">
                  {chevronIcon('appendices')}
                </span>
              </button>
              {isSectionOpen('appendices') && (
                <div className="hs-section-body">
                  {appendices.length > 0 ? (
                    <>
                      <div className="hs-sub-heading">Supporting Documents</div>
                      <div className="hs-table-wrapper">
                        <table className="hs-table hs-appendix-table">
                          <thead>
                            <tr>
                              <th>Document</th>
                              <th style={{ width: '120px', textAlign: 'right' }}>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {appendices.map((a, i) => (
                              <tr key={i}>
                                <td>
                                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--primary)" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                                    {a.name}
                                  </span>
                                </td>
                                <td style={{ textAlign: 'right' }}>
                                  <button className="hs-view-btn" onClick={() => handleViewAppendix(a.key)}>
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
                                    View
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="hs-sub-heading">Supporting Documents</div>
                      {emptyState(
                        <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="var(--border)" strokeWidth="1.5"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>

          </div>
        </>
      ) : (
        <div className="hs-doc-header">
          <div className="hs-doc-banner">
            <div className="hs-doc-banner-title">My Health Summary</div>
            <div className="hs-doc-banner-sub">Your health profile, available anytime, anywhere.</div>
          </div>
          <div className="hs-doc-owner" style={{ paddingBottom: '32px' }}>
            <p className="empty-state" style={{ margin: 0 }}>No health summary available yet. Your summary will appear here once it has been prepared.</p>
          </div>
        </div>
      )}

      {/* Patient Update Modal */}
      {showModal && (
        <PatientUpdateModal
          onClose={() => setShowModal(false)}
          onSubmitted={handleModalSubmitted}
        />
      )}

      <style>{`
        .health-summary p {
          color: var(--text-muted);
          line-height: 1.6;
          margin-bottom: 1rem;
        }

        /* Document Header Banner */
        .hs-doc-header {
          background: var(--white);
          border: 1px solid var(--border);
          border-radius: var(--radius);
          box-shadow: var(--shadow-sm);
          margin-bottom: 24px;
          overflow: hidden;
        }
        .hs-doc-banner {
          background: linear-gradient(135deg, rgb(80, 0, 108), rgb(110, 20, 145));
          padding: 24px 32px;
          text-align: center;
        }
        .hs-doc-banner-title {
          font-family: var(--font-display);
          font-size: 24px;
          color: var(--cream);
        }
        .hs-doc-banner-sub {
          font-size: 15px;
          color: var(--pink);
          margin-top: 4px;
        }
        .hs-doc-owner {
          text-align: center;
          padding: 28px 32px 16px;
        }
        .hs-doc-owner-label {
          font-family: var(--font-display);
          font-size: 18px;
          color: var(--text-muted);
          margin-bottom: 8px;
        }
        .hs-doc-owner-name {
          font-family: var(--font-display);
          font-size: 28px;
          color: var(--text);
          padding: 12px 0;
          border-top: 1px dashed var(--border);
          border-bottom: 1px dashed var(--border);
          margin: 8px 40px;
          letter-spacing: 0.5px;
        }
        .hs-doc-updated {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 6px 14px;
          background: var(--primary-bg);
          border-radius: 20px;
          font-size: 13px;
          font-weight: 600;
          color: var(--primary);
          margin-top: 16px;
        }
        .hs-doc-disclaimer {
          font-size: 11px;
          color: var(--text-light);
          margin-top: 6px;
          font-style: italic;
        }
        .hs-doc-actions {
          display: flex;
          gap: 10px;
          justify-content: center;
          padding: 16px 32px 22px;
          border-top: 1px solid var(--border);
          margin: 0 24px;
        }
        .hs-doc-btn {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          padding: 9px 18px;
          border-radius: var(--radius-sm);
          cursor: pointer;
          font-weight: 700;
          font-size: 14px;
          font-family: var(--font-body);
          transition: 0.15s;
          border: 1.5px solid var(--border);
        }
        .hs-doc-btn-outline {
          background: transparent;
          color: var(--text-muted);
        }
        .hs-doc-btn-outline:hover {
          border-color: var(--primary);
          color: var(--primary);
        }
        .hs-doc-btn-primary {
          background: var(--primary);
          color: var(--cream);
          border-color: var(--primary);
        }
        .hs-doc-btn-primary:hover {
          background: var(--primary-light);
          transform: translateY(-1px);
          box-shadow: 0 4px 16px rgba(80, 0, 108, 0.3);
        }

        /* Health Summary Sections */
        .hs-sections {
          display: flex;
          flex-direction: column;
          gap: 16px;
        }
        .hs-section {
          background: var(--white);
          border: 1px solid var(--border);
          border-radius: var(--radius);
          overflow: hidden;
        }
        .hs-section-title {
          color: var(--text);
          font-family: var(--font-display);
          font-size: 1.15rem;
          font-weight: 700;
          margin: 0;
          padding: 20px 24px;
          border: none;
          border-bottom: none;
          background: var(--white);
          width: 100%;
          text-align: left;
          display: flex;
          justify-content: space-between;
          align-items: center;
          cursor: pointer;
          user-select: none;
          transition: background 0.15s ease, color 0.15s ease;
        }
        .hs-section-title:hover {
          background: var(--primary-bg);
          color: var(--primary);
        }
        .hs-section-title.open {
          border-bottom: none;
          background: var(--primary-bg);
          color: var(--primary);
        }
        .hs-title-actions {
          display: flex;
          align-items: center;
          gap: 0.75rem;
        }
        .hs-section-body {
          padding: 4px 0 8px;
        }

        /* SVG Chevron */
        .hs-chevron {
          color: var(--text-light);
          transition: transform 0.2s ease;
          transform: rotate(0deg);
          flex-shrink: 0;
        }
        .hs-chevron.open {
          transform: rotate(90deg);
        }

        /* Sub-headings inside accordion body */
        .hs-sub-heading {
          font-weight: 700;
          font-size: 16px;
          color: var(--text);
          margin: 0 24px 12px;
          padding-top: 16px;
        }
        .hs-sub-desc {
          font-size: 14px;
          color: var(--text-muted);
          margin: -8px 24px 10px;
        }
        .hs-sub-row {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin: 0 24px;
        }
        .hs-divider {
          border-top: 1px solid var(--border);
          margin: 18px 24px;
        }

        /* Empty state */
        .hs-empty-state {
          padding: 24px;
          background: var(--bg);
          border-radius: 12px;
          text-align: center;
          margin: 0 24px 16px;
        }
        .hs-empty-state svg {
          margin-bottom: 10px;
        }
        .hs-empty-state div {
          font-size: 15px;
          color: var(--text-muted);
        }

        /* Add Entry button — hidden */
        .hs-add-entry-btn {
          display: none;
          background: var(--primary-bg);
          border: 1px solid var(--border);
          color: var(--primary);
          padding: 0.25rem 0.65rem;
          border-radius: 6px;
          font-size: 0.78rem;
          font-weight: 700;
          cursor: pointer;
          transition: background 0.2s, border-color 0.2s;
          white-space: nowrap;
          font-family: var(--font-body);
        }
        .hs-add-entry-btn:hover {
          background: var(--primary);
          color: var(--cream);
          border-color: var(--primary);
        }

        /* Patient-reported row highlight */
        .hs-patient-row {
          background: var(--amber-bg);
        }
        .hs-patient-row:hover {
          background: rgba(212, 168, 83, .18) !important;
        }

        /* Patient-reported badge */
        .hs-patient-badge {
          display: inline-block;
          background: var(--amber-bg);
          color: var(--amber);
          padding: 0.2rem 0.5rem;
          border-radius: 20px;
          font-size: 0.72rem;
          font-weight: 700;
          letter-spacing: 0.3px;
          white-space: nowrap;
        }

        /* Personal Info / Field-Value Grid */
        .hs-personal-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
          gap: 0.75rem;
          padding: 0 24px 16px;
        }
        .hs-personal-card {
          background: var(--bg);
          border: 1px solid var(--border);
          border-radius: var(--radius-sm);
          padding: 0.75rem 1rem;
          display: flex;
          flex-direction: column;
          gap: 0.25rem;
        }
        .hs-field-label {
          color: var(--text-muted);
          font-size: 0.8rem;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }
        .hs-field-value {
          color: var(--text);
          font-size: 1rem;
          font-weight: 500;
        }

        /* Tables */
        .hs-table-wrapper {
          overflow-x: auto;
          padding: 0 24px 16px;
        }
        .hs-table {
          width: 100%;
          border-collapse: collapse;
        }
        .hs-table th {
          text-align: left;
          color: var(--text-muted);
          font-size: 0.8rem;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          padding: 12px 16px;
          border-bottom: 2px solid var(--border);
          background: var(--bg);
        }
        .hs-table td {
          color: var(--text);
          padding: 14px 16px;
          border-bottom: 1px solid var(--border);
          font-size: 0.95rem;
        }
        .hs-table tbody tr:last-child td {
          border-bottom: none;
        }
        .hs-table tbody tr:hover {
          background: var(--primary-bg);
        }

        /* Severity badges */
        .hs-severity {
          display: inline-flex;
          padding: 4px 12px;
          border-radius: 20px;
          font-size: 0.8rem;
          font-weight: 700;
        }
        .hs-severity-mild {
          background: var(--primary-bg);
          color: var(--primary);
        }
        .hs-severity-moderate {
          background: var(--amber-bg);
          color: var(--amber);
        }
        .hs-severity-severe {
          background: var(--red-bg);
          color: var(--red);
        }

        /* Appendix link (inline in table cells) */
        .hs-appendix-link {
          color: var(--primary);
          text-decoration: underline;
          cursor: pointer;
          background: none;
          border: none;
          padding: 0;
          font: inherit;
          font-weight: 600;
        }
        .hs-appendix-link:hover {
          color: var(--primary-light);
        }

        /* Appendix table view button */
        .hs-view-btn {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 6px 14px;
          border: 1.5px solid var(--primary);
          border-radius: var(--radius-sm);
          background: transparent;
          color: var(--primary);
          font-size: 0.85rem;
          font-weight: 700;
          font-family: var(--font-body);
          cursor: pointer;
          transition: 0.15s;
        }
        .hs-view-btn:hover {
          background: var(--primary);
          color: var(--cream);
        }

        @media (max-width: 768px) {
          .hs-doc-banner {
            padding: 20px 20px;
          }
          .hs-doc-owner {
            padding: 20px 20px 12px;
          }
          .hs-doc-owner-name {
            font-size: 22px;
            margin: 8px 16px;
          }
          .hs-doc-actions {
            margin: 0 12px;
            padding: 14px 12px 18px;
            flex-wrap: wrap;
          }
          .hs-table th,
          .hs-table td {
            padding: 0.5rem;
            font-size: 0.85rem;
          }
          .hs-sections {
            gap: 12px;
          }
          .hs-section-title {
            padding: 16px;
            font-size: 1.05rem;
          }
          .hs-personal-grid {
            padding: 0 16px 16px;
          }
          .hs-table-wrapper {
            padding: 0 16px 16px;
          }
          .hs-add-entry-btn {
            font-size: 0.72rem;
            padding: 0.2rem 0.5rem;
          }
          .hs-sub-heading {
            margin-left: 16px;
            margin-right: 16px;
          }
          .hs-sub-desc {
            margin-left: 16px;
            margin-right: 16px;
          }
          .hs-divider {
            margin-left: 16px;
            margin-right: 16px;
          }
          .hs-empty-state {
            margin-left: 16px;
            margin-right: 16px;
          }
          .hs-sub-row {
            margin-left: 16px;
            margin-right: 16px;
          }
        }

        @media (max-width: 600px) {
          .hs-personal-grid {
            grid-template-columns: 1fr;
          }
          .hs-doc-actions {
            flex-direction: column;
          }
          .hs-doc-btn {
            width: 100%;
          }
        }

        @media print {
          .hs-doc-actions,
          .hs-add-entry-btn,
          .hs-chevron,
          .hs-view-btn {
            display: none !important;
          }
          .hs-appendix-link {
            color: var(--primary);
            text-decoration: underline;
          }
          .hs-section-title {
            background: var(--primary-bg) !important;
            color: var(--primary) !important;
          }
          .hs-section {
            break-inside: avoid;
          }
        }
      `}</style>
    </div>
  );
};

export default HealthSummary;
